import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import { Close as CloseIcon, Search } from '@material-ui/icons';
import { gql } from '@apollo/client';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';

const GET_PROGRAMS_WITH_ACTIVITIES = gql`
  query GetProgramsWithActivitiesForChat {
    vm {
      programs {
        programId
        name
        activities(includeInactiveActivities: false) {
          activityId
          name
          startDate
          endDate
          program {
            programId
            name
          }
        }
      }
    }
  }
`;

interface Activity {
  activityId: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  program: { programId: string; name: string } | null;
}

interface ShareActivityDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string, metadata: Record<string, unknown>) => void;
}

export const ShareActivityDialog = ({ open, onClose, onSubmit }: ShareActivityDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [runQuery, { data, loading }] = useCampfireLazyQuery<any, any>(GET_PROGRAMS_WITH_ACTIVITIES);

  useEffect(() => {
    if (open) {
      runQuery();
    }
  }, [open]);

  const allActivities: Activity[] = (data?.vm?.programs || []).flatMap((p: any) => p.activities || []);

  const filtered = searchTerm.trim()
    ? allActivities.filter((a) => a.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : allActivities;

  const handleSelect = (activity: Activity) => {
    onSubmit('Shared an activity', {
      type: 'activity_link',
      activityId: activity.activityId,
    });
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <Typography style={{ fontWeight: 700, fontSize: '1.15rem', color: '#333' }}>Share Activity</Typography>
            <Typography variant='body2' style={{ color: '#999', marginTop: 2 }}>
              Select an activity to share in the conversation
            </Typography>
          </Grid>
          <Grid item>
            <IconButton aria-label='Close' size='small' onClick={handleClose} style={{ marginTop: -4 }}>
              <CloseIcon style={{ fontSize: 20, color: '#999' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16, paddingBottom: 20 }}>
        <TextField
          fullWidth
          variant='outlined'
          placeholder='Search activities...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: 16 }}
          InputProps={{
            style: { borderRadius: 10, fontSize: '0.9rem' },
            startAdornment: (
              <InputAdornment position='start'>
                <Search style={{ color: '#bbb', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        {loading && (
          <Box display='flex' justifyContent='center' p={3}>
            <CircularProgress size={24} />
          </Box>
        )}
        <List style={{ maxHeight: 350, overflow: 'auto' }} disablePadding>
          {filtered.map((activity) => (
            <ListItem
              key={activity.activityId}
              button
              onClick={() => handleSelect(activity)}
              style={{ borderRadius: 8, marginBottom: 2, paddingTop: 8, paddingBottom: 8 }}
            >
              <ListItemText
                primary={activity.name}
                secondary={activity.program?.name}
                primaryTypographyProps={{ style: { fontWeight: 500, fontSize: '0.9rem', color: '#333' } }}
                secondaryTypographyProps={{ style: { fontSize: '0.8rem' } }}
              />
            </ListItem>
          ))}
          {!loading && filtered.length === 0 && (
            <Box p={3} textAlign='center'>
              <Typography variant='body2' style={{ color: '#999' }}>
                No activities found
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};
